/**
 * Internal Hook: useUpdateMeetingMutation
 *
 * Encapsulates the TanStack Query mutation logic for updating a scheduled meeting.
 * It provides a responsive user experience by leveraging optimistic updates:
 * - `onMutate`: Immediately updates the meeting in the cache for instant UI feedback.
 * - `onSuccess`: Replaces optimistic meeting with real server data.
 * - `onError`: Rolls back the cache to its previous state on failure.
 * - `onSettled`: Refetches the meeting list and details to ensure data consistency.
 *   Also handles cache cleanup if a meeting's room ID was changed.
 */
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { merge } from 'lodash-es';
import { SCHEDULED_MEETINGS_QUERY_KEYS } from '../helpers';
import type {
  ScheduledMeeting,
  ScheduledMeetingsProviderOptions,
  UpdateMeetingParams,
} from '../types';
import useScheduledMeetingsApi from './useScheduledMeetingsApi';

const useUpdateMeetingMutation = (options: ScheduledMeetingsProviderOptions) => {
  const queryClient = useQueryClient();
  const api = useScheduledMeetingsApi();

  return useMutation({
    mutationFn: async (params: UpdateMeetingParams) => {
      const response = await api.updateMeeting(params);
      return response as ScheduledMeeting;
    },
    onMutate: async (params) => {
      // Cancel outgoing refetches
      const queryKey = SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery);
      await queryClient.cancelQueries({ queryKey });

      const previousMeetings = queryClient.getQueryData<InfiniteData<ScheduledMeeting[]>>(queryKey);

      // Optimistically update
      if (previousMeetings) {
        queryClient.setQueryData<InfiniteData<ScheduledMeeting[]>>(queryKey, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) =>
              page.map((meeting) => {
                if (meeting.room === params.room) {
                  const updatedMeeting = merge({}, meeting, params);

                  if (params.new_room) {
                    updatedMeeting.room = params.new_room;
                  }
                  return updatedMeeting;
                }

                return meeting;
              }),
            ),
          };
        });
      }

      return { previousMeetings };
    },
    onSuccess: (serverMeeting, params) => {
      const queryKey = SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery);

      queryClient.setQueryData<InfiniteData<ScheduledMeeting[]>>(queryKey, (oldData) => {
        if (!oldData?.pages) {
          return oldData;
        }

        const pages = oldData.pages.map((page) => {
          const hasMeeting = page.some(
            (meeting) => meeting.room === params.room || meeting.room === params.new_room,
          );

          if (!hasMeeting) {
            return page;
          }

          return page.map((meeting) => {
            if (meeting.room === params.room || meeting.room === params.new_room) {
              return {
                ...meeting,
                ...serverMeeting,
                options: params.options ?? meeting.options,
                domains: params.domains ?? meeting.domains,
              };
            }
            return meeting;
          });
        });

        return { ...oldData, pages };
      });
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousMeetings) {
        queryClient.setQueryData(
          SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery),
          context.previousMeetings,
        );
      }
    },
    onSettled: (updatedMeeting, error, params) => {
      // Remove old detail if meeting ID has changed (it's garbage now)
      if (!error && params.new_room && params.room !== params.new_room) {
        queryClient.removeQueries({
          queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.detail(params.room),
        });
      }

      // Invalidate detail for this specific meeting
      if (updatedMeeting) {
        queryClient.invalidateQueries({
          queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.detail(updatedMeeting.room),
        });
      }

      // Invalidate all lists
      queryClient.invalidateQueries({
        queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.lists(),
      });
    },
  });
};

export default useUpdateMeetingMutation;
